import React, { useState, useEffect, useRef } from "react";
import DayTimetable from "../../Components/dayTimetable";
import DateSelector from "../../Components/dateSelector";
import ScholWeekSelector from "../../Components/schoolWeekSelector";
import ClassStatsGrid from "../../Components/classStatsGrid";
import { NumberInput, Toggle } from "@carbon/react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { gql, useLazyQuery, useQuery, useMutation } from "@apollo/client";
import TimetableClassEditor from "../../Components/timetableClassEditor";

// Lightweight query for the teacher/admin view. We intentionally request
// only commonly-needed fields to keep the response small.
const GET_SCHOOL_CLASS = gql`
    query SchoolClassById($id: String!) {
        schoolClassById(id: $id) {
            id
            name
            senior
            editing
            teacher { id username surname lastname }
            stats { studies exits }
            timePlanString
        }
        me {
            __typename
            id
            username
            surname
            lastname
        }
    }
`;

// Query for time units/slots
const GET_TIME_UNITS = gql`
    query GetTimeUnits {
        timeUnitsPlan {
            index
            startTime
            endTime
        }
    }
`;

// Mutation to update class timetable
const SET_CLASS_TIMETABLE = gql`
    mutation SetClassTimetable($classId: String!, $timetable: [[String!]!]!) {
        setClassTimetable(classId: $classId, timetable: $timetable)
    }
`;

// Query for school year overview to fetch day names
const GET_SCHOOL_YEAR_OVERVIEW = gql`
    query GetSchoolYearOverview {
        schoolYearOverview {
            dayNames
            dayShortNames
        }
    }
`;

// Mutation to update class state
const SET_CLASS_STATE = gql`
    mutation SetClassState($classUid: String!, $exits: Int!, $studies: Int!, $editing: Boolean!) {
        setClassState(classUid: $classUid, exits: $exits, studies: $studies, editing: $editing)
    }
`;

export default function TeacherClassEdit({ selectedClass }) {

    const [fetchSchoolClass, { data: classData, loading: classLoading, error: classError }] = useLazyQuery(GET_SCHOOL_CLASS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
    });

    // Fetch time units
    const { data: timeUnitsData, loading: timeUnitsLoading, error: timeUnitsError } = useQuery(GET_TIME_UNITS);
    
    // State for day names fetched from server
    const [dayLabels, setDayLabels] = useState([]);
    // Track whether there are unsaved changes in the timetable
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Fetch school year overview
    const { data: schoolYearData, loading: schoolYearLoading, error: schoolYearError } = useQuery(GET_SCHOOL_YEAR_OVERVIEW);
    
    // Mutation to update the class timetable
    const [setClassTimetable, { loading: savingTimetable, error: saveTimetableError }] = useMutation(SET_CLASS_TIMETABLE);
    // Mutation to update the class state
    const [setClassState] = useMutation(SET_CLASS_STATE);

    useEffect(() => {
        if (selectedClass) {
            fetchSchoolClass({ variables: { id: selectedClass } });
        }
    }, [selectedClass, fetchSchoolClass]);
    
    // Router navigate for redirecting on errors
    const navigate = useNavigate();

    // Redirect to root if any query returns an error
    useEffect(() => {
        if (classError || timeUnitsError || schoolYearError) {
            const errorMessage = classError?.message || timeUnitsError?.message || schoolYearError?.message;
            console.error('Error fetching data, redirecting to /:', errorMessage);
            navigate('/', { replace: true });
        }
    }, [classError, timeUnitsError, schoolYearError, navigate]);

    // Process school year data when it arrives
    useEffect(() => {
        if (schoolYearData?.schoolYearOverview?.dayNames) {
            setDayLabels(schoolYearData.schoolYearOverview.dayNames);
        }
    }, [schoolYearData]);

    // New: matrix state that mirrors the timetable and a helper to build it
    const [matrix, setMatrix] = useState([]);

    const [editing, setEditing] = useState(classData?.schoolClassById?.editing || false);
    const [classStudies, setClassStudies] = useState(classData?.schoolClassById?.stats?.studies || 0);
    const [classExts, setClassExts] = useState(classData?.schoolClassById?.stats?.exits || 0);

    //use effect for studies and exts and editing when classData changes
    useEffect(() => {
        setEditing(classData?.schoolClassById?.editing || false);
        setClassStudies(classData?.schoolClassById?.stats?.studies || 0);
        setClassExts(classData?.schoolClassById?.stats?.exits || 0);
    }, [classData]);

    const transpose = (m) => {
        if (!Array.isArray(m) || m.length === 0) return [];
        // assume m is rows x cols and return cols x rows
        return m[0].map((_, c) => m.map(r => (Array.isArray(r) ? r[c] : r)));
    };

    const buildMatrixFromTimetable = (timetable) => {
        if (!timetable) return [];
        // If it's already an array, determine whether it's rows×cols or cols×rows
        if (Array.isArray(timetable)) {
            if (Array.isArray(timetable[0])) {
                // If outer length is greater than inner length, it's likely rows×cols -> transpose
                if (timetable.length > timetable[0].length) {
                    return transpose(timetable);
                }
                // Otherwise assume it's already cols×rows and return as-is
                return timetable;
            }
            // not a nested array, treat as a single column (one col with many rows)
            return [timetable];
        }
        // If it's a serialized string, try to parse it
        try {
            const parsed = JSON.parse(timetable);
            if (Array.isArray(parsed) && Array.isArray(parsed[0])) {
                if (parsed.length > parsed[0].length) {
                    return transpose(parsed);
                }
                return parsed;
            }
            return Array.isArray(parsed) ? parsed : [[parsed]];
        } catch (e) {
            // Fallback: wrap value so consumers still get a structure
            console.warn('Failed to parse timetable into matrix, falling back to array wrap', e);
            return [[timetable]];
        }
    };

    // Initialize / update matrix whenever the fetched timetable changes
    useEffect(() => {
        setMatrix(buildMatrixFromTimetable(classData?.schoolClassById?.timePlanString || []));
    }, [classData?.schoolClassById?.timePlanString]);

    // Handler bound to the TimetableClassEditor change callback
    const handleTimetableChange = (colIndex, rowIndex, value) => {
        setMatrix((prevMatrix) => {
            // deep clone columns
            const updatedMatrix = (prevMatrix || []).map(col => Array.isArray(col) ? [...col] : []);
            if (!updatedMatrix[colIndex]) updatedMatrix[colIndex] = [];
            updatedMatrix[colIndex][rowIndex] = value;
            return updatedMatrix;
        });
        
        // Mark that we have unsaved changes
        setHasUnsavedChanges(true);
    };
    
    // Function to save timetable changes to the server
    const handleSaveTimetable = async () => {
        if (!selectedClass || !matrix) return;

        try {
            const result = await setClassTimetable({
                variables: {
                    classId: selectedClass,
                    timetable: matrix // Pass the matrix directly as the timetable
                }
            });

            if (result.data?.setClassTimetable) {
                // Reset unsaved changes flag on successful save
                setHasUnsavedChanges(false);

                // Optionally refetch class data to ensure we're in sync with server
                fetchSchoolClass({ variables: { id: selectedClass } });
            }
        } catch (err) {
            console.error("Error saving timetable:", err);
        }
    };

    // Update class state instantly when inputs change
    const handleClassStateChange = async (updates) => {
        if (!selectedClass) return;

        try {
            const result = await setClassState({
                variables: {
                    classUid: selectedClass,
                    exits: updates.exits ?? classExts,
                    studies: updates.studies ?? classStudies,
                    editing: updates.editing ?? editing,
                },
            });

            if (result.data?.setClassState) {
                // Optionally refetch class data to ensure we're in sync with server
            }
        } catch (err) {
            console.error("Error updating class state:", err);
        }
    };

    if (!selectedClass) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">
                    Keine Klasse ausgewählt
                </p>
            </section>
        );
    }

    // Show loading state if any of the queries are still loading
    if (classLoading || timeUnitsLoading || schoolYearLoading) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">
                    Lade Daten...
                </p>
            </section>
        );
    }

    // Check for any errors from the queries
    if (classError || timeUnitsError || schoolYearError) {
        // Navigation will redirect the user to '/', so render nothing here.
        return null;
    }
    
    // Prepare time units from the query result, sorted by index
    const timeUnits = timeUnitsData?.timeUnitsPlan?.slice() || [];
    if (timeUnits.length > 0) {
        timeUnits.sort((a, b) => a.index - b.index);
    }

    // Determine whether the current user can view the timetable
    const meType = classData?.me?.__typename;
    const meUsername = classData?.me?.username;
    const classTeacherUsername = classData?.schoolClassById?.teacher?.username;
    // Admins can always view. Non-teachers (e.g. students) can view. Teachers can view only if their username matches the class teacher.
    const canViewTimetable = meType === 'Admin' || meType !== 'Teacher' || (meUsername && classTeacherUsername && meUsername === classTeacherUsername);

    return (
        <section className="container mx-auto">
            <div className="my-6 flex justify-between items-center">
                <p className="text-gray-800 text-2xl font-semibold">
                    Klasseneinstellungen für {classData?.schoolClassById?.name || 'Lade...'}
                </p>
            </div>

            {classData?.schoolClassById?.senior && (
                <div className="my-4 flex-col md:flex-row flex md:items-center md:justify-between">
                    <NumberInput
                        id="classStudies"
                        label="Anzahl der Studienstunden pro Woche"
                        min={0}
                        value={classStudies || 0}
                        onChange={(evt, { value }) => {
                            setClassStudies(value);
                            handleClassStateChange({ studies: value });
                        }}
                        className="w-full md:w-2/5 mb-4 md:mb-0"
                    />
                    <NumberInput
                        id="classExts"
                        label="Anzahl der Erweiterungsstunden pro Woche"
                        min={0}
                        value={classExts || 0}
                        onChange={(evt, { value }) => {
                            setClassExts(value);
                            handleClassStateChange({ exits: value });
                        }}
                        className="w-full md:w-2/5 mb-4 md:mb-0 md:mx-4"
                    />
                    <Toggle
                        id="classEditing"
                        labelText="Klasse im Bearbeitungsmodus"
                        toggled={editing || false}
                        onToggle={(toggled) => {
                            setEditing(toggled);
                            handleClassStateChange({ editing: toggled });
                        }}
                        className="w-full md:w-1/5"
                    />
                </div>
            )}

            
            <div className="my-2">
                {canViewTimetable ? (
                    <>
                        <TimetableClassEditor
                            timetable={matrix}
                            onTimetableChange={handleTimetableChange}
                            dayLabels={dayLabels}
                            dayShortNames={schoolYearData?.schoolYearOverview?.dayShortNames || []}
                            timeUnits={timeUnits}
                        />
                        <div className="my-4 flex items-center justify-between px-2 items-center">
                            <p className="text-gray-700 font-medium">
                            {saveTimetableError && (
                                <p className="text-red-600">Fehler beim Speichern: {saveTimetableError.message}</p>
                            )}
                            </p>
                            <button
                                className={`px-5 py-3 bg-gray-600 text-white ${hasUnsavedChanges ? 'hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                                onClick={handleSaveTimetable}
                                disabled={!hasUnsavedChanges || savingTimetable}
                            >
                                {savingTimetable ? 'Speichern...' : 'Änderungen speichern'}
                            </button>
                            
                        </div>
                    </>
                ) : (
                    <div className="my-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700">
                        Sie sehen hier keinen Stundenplan — Sie sind nicht der zuständige Lehrer dieser Klasse.
                    </div>
                )}
            </div>
        </section>
    );
}
